import { useMemo } from 'react';
import {
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { EntityFormDialog } from '../../components/dialogs/EntityFormDialog';
import {
  useCreateSite,
  useEditSite,
  useMe,
  useOperatorsList,
} from '../../api/queries';
import { useQuery } from '@tanstack/react-query';
import type { SiteSummary } from '../../api/types';
import { COMMON_TIMEZONES, timeZoneLabel } from '../../utils/timezones';

const schema = z.object({
  operatorId: z.string().uuid().or(z.literal('')).optional(),
  code: z.string()
    .trim()
    .min(2, 'At least 2 characters')
    .max(40, 'At most 40 characters')
    .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/, 'Letters, digits, _ and - only; must start with a letter or digit'),
  displayName:   z.string().trim().min(1, 'Required').max(100),
  addressLine1:  z.string().trim().min(1, 'Required').max(200),
  addressLine2:  z.string().trim().max(200).optional()
                  .or(z.literal('').transform(() => undefined)),
  city:          z.string().trim().min(1, 'Required').max(80),
  stateProvince: z.string().trim().max(40).optional()
                  .or(z.literal('').transform(() => undefined)),
  postalCode:    z.string().trim().max(20).optional()
                  .or(z.literal('').transform(() => undefined)),
  timeZone:      z.string().min(1, 'Required'),
  // 'stable' (default) or 'beta'. PR 1 software-rollout work.
  releaseChannel: z.enum(['stable', 'beta']).default('stable'),
  // Minutes after local midnight the site's gaming day starts (0-1439).
  // 0 = calendar day; e.g. 360 = gaming day runs 06:00-05:59.
  gamingDayOffsetMinutes: z.coerce.number().int().min(0, 'Must be ≥ 0').max(1439, 'Must be < 1440'),
});

export type SiteFormValues = z.infer<typeof schema>;

export type SiteFormDialogProps = {
  open: boolean;
  mode: 'create' | 'edit';
  site?: SiteSummary;
  onClose: () => void;
};

export function SiteFormDialog({ open, mode, site, onClose }: SiteFormDialogProps) {
  const me = useMe();
  const isArms = me.data?.is_arms ?? false;
  const create = useCreateSite();
  const edit = useEditSite(site?.id ?? '');

  const initial: SiteFormValues = mode === 'edit' && site
    ? {
        operatorId:    site.operatorId,
        code:          site.code,
        displayName:   site.displayName,
        addressLine1:  site.addressLine1,
        addressLine2:  '',
        city:          site.city,
        stateProvince: site.stateProvince,
        postalCode:    site.postalCode,
        timeZone:      site.timeZone,
        releaseChannel: (site.releaseChannel === 'beta' ? 'beta' : 'stable'),
        gamingDayOffsetMinutes: site.gamingDayOffsetMinutes,
      }
    : {
        operatorId:    isArms ? '' : (me.data?.operator_id ?? ''),
        code:          '',
        displayName:   '',
        addressLine1:  '',
        addressLine2:  '',
        city:          '',
        stateProvince: '',
        postalCode:    '',
        timeZone:      'America/New_York',
        releaseChannel: 'stable',
        gamingDayOffsetMinutes: 0,
      };

  const submit = async (values: SiteFormValues) => {
    if (mode === 'edit') {
      await edit.mutateAsync({
        displayName:   values.displayName,
        addressLine1:  values.addressLine1,
        addressLine2:  values.addressLine2 ?? null,
        city:          values.city,
        stateProvince: values.stateProvince ?? null,
        postalCode:    values.postalCode ?? null,
        timeZone:      values.timeZone,
        releaseChannel: values.releaseChannel,
        gamingDayOffsetMinutes: values.gamingDayOffsetMinutes,
      });
    } else {
      // 'create' doesn't push releaseChannel — server defaults to
      // 'stable'. Operator can promote via 'edit' afterward.
      await create.mutateAsync({
        operatorId:    values.operatorId || null,
        code:          values.code,
        displayName:   values.displayName,
        addressLine1:  values.addressLine1,
        addressLine2:  values.addressLine2 ?? null,
        city:          values.city,
        stateProvince: values.stateProvince ?? null,
        postalCode:    values.postalCode ?? null,
        timeZone:      values.timeZone,
        gamingDayOffsetMinutes: values.gamingDayOffsetMinutes,
      });
    }
  };

  return (
    <EntityFormDialog<SiteFormValues>
      open={open}
      mode={mode}
      title={mode === 'create' ? 'Create site' : 'Edit site'}
      schema={schema}
      initialValues={initial}
      onSubmit={submit}
      onClose={onClose}
    >
      <Fields mode={mode} isArms={isArms} />
    </EntityFormDialog>
  );
}

function Fields({ mode, isArms }: { mode: 'create' | 'edit'; isArms: boolean }) {
  const { register, control, formState: { errors } } = useFormContext<SiteFormValues>();
  const operators = useOperatorsList();
  const operatorsQ = useQuery({
    queryKey: operators.queryKey,
    queryFn: operators.listFn,
    enabled: isArms && mode === 'create',
  });
  const operatorOptions = useMemo(() => operatorsQ.data ?? [], [operatorsQ.data]);

  return (
    <>
      {isArms && mode === 'create' && (
        <Controller
          name="operatorId"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select
              label="Operator"
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? 'Tenant the site belongs to.'}
              fullWidth
              {...field}
              value={field.value ?? ''}
            >
              <MenuItem value="">— select —</MenuItem>
              {operatorOptions.map((op) => (
                <MenuItem key={op.id} value={op.id}>
                  {op.displayName} ({op.code})
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      )}

      <TextField
        label="Code"
        size="small"
        disabled={mode === 'edit'}
        helperText={mode === 'edit'
          ? 'Code is immutable.'
          : (errors.code?.message ?? 'Letters, digits, _ and -; must start with a letter or digit.')}
        error={!!errors.code}
        fullWidth
        autoFocus={mode === 'create'}
        {...register('code')}
      />
      <TextField
        label="Display name"
        size="small"
        helperText={errors.displayName?.message}
        error={!!errors.displayName}
        fullWidth
        {...register('displayName')}
      />
      <TextField
        label="Address line 1"
        size="small"
        helperText={errors.addressLine1?.message}
        error={!!errors.addressLine1}
        fullWidth
        {...register('addressLine1')}
      />
      <TextField
        label="Address line 2 (optional)"
        size="small"
        helperText={errors.addressLine2?.message}
        error={!!errors.addressLine2}
        fullWidth
        {...register('addressLine2')}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="City"
          size="small"
          helperText={errors.city?.message}
          error={!!errors.city}
          sx={{ flex: 1.5 }}
          {...register('city')}
        />
        <TextField
          label="State / province"
          size="small"
          helperText={errors.stateProvince?.message}
          error={!!errors.stateProvince}
          sx={{ flex: 1 }}
          {...register('stateProvince')}
        />
        <TextField
          label="Postal code"
          size="small"
          helperText={errors.postalCode?.message}
          error={!!errors.postalCode}
          sx={{ flex: 1 }}
          {...register('postalCode')}
        />
      </Stack>

      <Controller
        name="timeZone"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            select
            label="Time zone"
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            fullWidth
            {...field}
          >
            {COMMON_TIMEZONES.map((tz) => (
              <MenuItem key={tz} value={tz}>{timeZoneLabel(tz)}</MenuItem>
            ))}
          </TextField>
        )}
      />

      <TextField
        label="Gaming day offset (minutes)"
        size="small"
        type="number"
        inputProps={{ min: 0, max: 1439 }}
        helperText={errors.gamingDayOffsetMinutes?.message ??
          '0 = midnight; 360 = gaming day runs 06:00–05:59'}
        error={!!errors.gamingDayOffsetMinutes}
        sx={{ flex: 1 }}
        {...register('gamingDayOffsetMinutes')}
      />

      {/* Release channel — only on Edit (Create defaults server-side to
          stable; an operator promotes a site to beta later). */}
      {mode === 'edit' && (
        <Controller
          name="releaseChannel"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select
              label="Release channel"
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message ??
                'Beta sites pull pre-release sc-runtime updates. SCs follow within ~5 min of a change.'}
              fullWidth
              {...field}
            >
              <MenuItem value="stable">Stable (production)</MenuItem>
              <MenuItem value="beta">Beta (test releases)</MenuItem>
            </TextField>
          )}
        />
      )}
    </>
  );
}
